import React, { useState } from 'react'
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import HomeIcon from '@mui/icons-material/Home'
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom'
import PeopleIcon from '@mui/icons-material/People'
import LinkIcon from '@mui/icons-material/Link'
import LockIcon from '@mui/icons-material/Lock'
import RefreshIcon from '@mui/icons-material/Refresh'

import type { Room } from './RoomCard'
import { ActionButton } from './styles/RoomCard.styles'
import { useAppSelector } from '../hooks'
import { copyRoomJoinLink } from '../utils/roomJoinLink'
import DashboardPanel, { DashboardPanelEmpty } from './DashboardPanel'

export interface DashboardRoomListProps {
  rooms: Room[]
  onJoin: (room: Room) => Promise<void> | void
  /** Joining waits for the lobby connection. */
  disabled?: boolean
  /** The room list is loading. */
  loading?: boolean
  refresh?: {
    onRefresh: () => void
    tooltip: string
    disabled: boolean
    /** A refresh is running: the button shows a spinner. */
    busy: boolean
  }
}

/** The Dashboard's company rooms, one compact row per room (SC-422). */
export default function DashboardRoomList({ rooms, onJoin, disabled = false, loading = false, refresh }: DashboardRoomListProps) {
  const userCompanyId = useAppSelector((s) => s.user.userData?.companyId ?? null)
  const [joiningId, setJoiningId] = useState<string | null>(null)

  const handleJoin = async (room: Room) => {
    setJoiningId(room.id)
    try {
      await onJoin(room)
    } catch (err) {
      console.error('Error joining room:', err)
    } finally {
      setJoiningId(null)
    }
  }

  const handleCopyLink = async (room: Room) => {
    try {
      await copyRoomJoinLink(room, userCompanyId)
    } catch (err) {
      console.error('Failed to copy join link', err)
    }
  }

  return (
    <DashboardPanel
      title="Company rooms"
      count={rooms.length}
      data-testid="dashboard-room-list"
      action={
        refresh && (
          <Tooltip title={refresh.tooltip}>
            <span>
              <IconButton size="small" aria-label="Refresh rooms" onClick={refresh.onRefresh} disabled={refresh.disabled} color="primary">
                {refresh.busy ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )
      }
    >
      {rooms.length === 0 &&
        (loading ? (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <DashboardPanelEmpty>No rooms yet. Your company admins can add rooms.</DashboardPanelEmpty>
        ))}
      {rooms.map((room) => {
        const isLobby = !!room.metadata?.isLobby
        const isAtCapacity = room.maxCapacity !== undefined && room.members >= room.maxCapacity
        const joining = joiningId === room.id
        return (
          <Box
            key={room.id}
            data-testid="dashboard-room-row"
            sx={(t) => ({
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1,
              '&:not(:last-of-type)': { borderBottom: '1px solid', borderColor: 'divider' },
              '&:hover': { backgroundColor: alpha(t.palette.primary.main, 0.04) },
            })}
          >
            {isLobby ? (
              <Tooltip title="Company lobby">
                <HomeIcon color="primary" fontSize="small" />
              </Tooltip>
            ) : (
              <MeetingRoomIcon color="primary" fontSize="small" />
            )}
            <Typography
              variant="body2"
              noWrap
              title={room.name}
              sx={{ flex: 1, minWidth: 0, fontWeight: 600, color: 'text.primary' }}
            >
              {room.name}
            </Typography>

            <Tooltip title="Players in the room">
              <Box
                data-testid="dashboard-room-players"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', fontSize: '0.85rem', fontWeight: 600 }}
              >
                <PeopleIcon sx={{ fontSize: '1rem' }} />
                {room.maxCapacity ? `${room.members}/${room.maxCapacity}` : room.members}
              </Box>
            </Tooltip>

            <Tooltip title="Copy join link">
              <span>
                <IconButton
                  size="small"
                  aria-label="Copy join link"
                  onClick={() => handleCopyLink(room)}
                  disabled={joining}
                  sx={(t) => ({
                    color: 'text.secondary',
                    border: `1px solid ${t.palette.divider}`,
                    borderRadius: '6px',
                    p: 0.5,
                    '&:hover': {
                      backgroundColor: alpha(t.palette.primary.main, 0.1),
                      borderColor: 'primary.main',
                      color: 'primary.main',
                    },
                  })}
                >
                  <LinkIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <ActionButton
              variant="contained"
              color="primary"
              size="small"
              onClick={() => handleJoin(room)}
              disabled={joining || isAtCapacity || disabled}
              startIcon={!room.isPublic && !joining && !disabled ? <LockIcon fontSize="small" /> : undefined}
              sx={{ py: 0.5, px: 2 }}
            >
              {joining ? <CircularProgress size={18} color="inherit" /> : isAtCapacity ? 'Full' : disabled ? 'Connecting…' : 'Join'}
            </ActionButton>
          </Box>
        )
      })}
    </DashboardPanel>
  )
}
